import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Modal, Card, Input } from "@/components/erp/ui";
import { ShieldCheck, Play, Timer } from "lucide-react";

const t = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const mins = (m) => (m == null ? "—" : m >= 60 ? `${Math.floor(m / 60)}j ${Math.round(m % 60)}m` : `${Math.round(m)}m`);
const err = (e, d) => toast.error(e?.response?.data?.detail || d);

export const ConfirmBar = ({ wo, onDone }) => {
  if (wo.confirmed_at) {
    return <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 inline-flex items-center gap-1.5" data-testid="wo-active-badge"><ShieldCheck className="w-4 h-4" /> WO AKTIF sejak {t(wo.confirmed_at)} · dikonfirmasi {wo.confirmed_by}</div>;
  }
  const confirm = async () => {
    try { const { data } = await api.post(`/workorders/${wo.id}/confirm`); toast.success("WO dikonfirmasi & aktif — waktu tunggu mulai dihitung"); onDone(data); }
    catch (e) { err(e, "Gagal konfirmasi"); }
  };
  return (
    <div className="mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50 flex flex-wrap items-center gap-2" data-testid="wo-confirm-bar">
      <span className="text-xs text-amber-800 flex-1">WO belum aktif. Cek PIC tiap tahap, lalu konfirmasi supaya waktu tunggu mulai dihitung.</span>
      <Btn data-testid="wo-confirm-btn" onClick={confirm}><ShieldCheck className="w-4 h-4" /> Konfirmasi WO</Btn>
    </div>
  );
};

export const StageSla = ({ wo, s, tol, onDone }) => {
  if (!wo.confirmed_at || !s.ready_at) return null;
  const waiting = !s.picked_at && !["COMPLETED", "SKIPPED"].includes(s.status);
  const wm = waiting ? (Date.now() - new Date(s.ready_at)) / 60000 : s.wait_min;
  const over = waiting ? wm > tol : !!s.late;
  const pick = async () => {
    try { const { data } = await api.post(`/workorders/${wo.id}/pick`, { stage: s.name }); toast.success(`${s.name} mulai dikerjakan`); onDone(data); }
    catch (e) { err(e, "Gagal"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1.5 pl-5 text-[11px]" data-testid={`stage-sla-${s.id || s.name}`}>
      <Timer className="w-3.5 h-3.5 text-slate-400" />
      <span className="text-slate-500">Siap {t(s.ready_at)}</span>
      {s.picked_at && <span className="text-slate-500">· mulai {t(s.picked_at)} oleh <b>{s.picked_by}</b></span>}
      <span className={`px-1.5 rounded font-semibold ${s.excused ? "bg-slate-100 text-slate-600" : over ? "bg-rose-100 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`stage-wait-${s.id || s.name}`}>
        tunggu {mins(wm)}{s.excused ? " · dimaklumi (tim penuh)" : over ? " · lewat toleransi" : ""}
      </span>
      {s.completed_at && <span className="text-slate-500">· selesai {t(s.completed_at)}</span>}
      {waiting && <button type="button" onClick={pick} data-testid={`stage-pick-${s.id || s.name}`} className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900 text-white"><Play className="w-3 h-3" /> Mulai kerjakan</button>}
    </div>
  );
};

export const useSlaTol = () => {
  const [tol, setTol] = useState(60);
  useEffect(() => { api.get("/workorders/sla/settings").then(({ data }) => setTol(data.handoff_tolerance_min)).catch(() => {}); }, []);
  return [tol, setTol];
};

export const SlaReportModal = ({ open, onClose, owner }) => {
  const [d, setD] = useState(null);
  const [tol, setTol] = useState("");
  const load = () => api.get("/workorders/sla/report").then(({ data }) => { setD(data); setTol(data.tolerance); }).catch(() => {});
  useEffect(() => { if (open) { load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => { try { await api.put("/workorders/sla/settings", { handoff_tolerance_min: +tol }); toast.success("Toleransi disimpan"); load(); } catch (e) { err(e, "Gagal"); } };
  return (
    <Modal open={open} onClose={onClose} wide title="Laporan Waktu Tunggu (WO keluar → diproses)">
      {d && (
        <div className="space-y-4" data-testid="sla-report">
          <div className="flex flex-wrap items-end gap-2 text-xs text-slate-500">Toleransi tunggu per tahap:
            {owner ? <><div className="w-24"><Input type="number" data-testid="sla-tolerance" value={tol} onChange={(e) => setTol(e.target.value)} /></div><span>menit</span><Btn variant="ghost" data-testid="sla-tolerance-save" onClick={save}>Simpan</Btn></> : <b>{d.tolerance} menit</b>}
            <span>· tidak dihitung terlambat bila semua orang di tahap itu sedang ada kerjaan</span>
          </div>
          <Card className="p-3" data-testid="sla-waiting-now"><div className="text-sm font-semibold mb-1 text-rose-700">Sedang menunggu lewat toleransi ({d.waiting_now.length})</div>
            {d.waiting_now.map((x) => <div key={x.wo_id + x.stage} className="text-xs py-0.5">{x.wo_code} · <b>{x.stage}</b> · PIC {x.pic || "—"} · {mins(x.wait_min)}</div>)}
            {!d.waiting_now.length && <div className="text-xs text-slate-400">Tidak ada</div>}
          </Card>
          <Card className="p-3" data-testid="sla-people"><div className="text-sm font-semibold mb-1">Per orang</div>
            {Object.entries(d.people).map(([n, p]) => <div key={n} className="text-xs py-0.5 flex"><span className="flex-1">{n}</span><span>{p.picks} tahap · rata-rata tunggu {mins(p.avg_wait)} · terlambat <b className={p.late ? "text-rose-600" : ""}>{p.late}</b> · dimaklumi {p.excused}</span></div>)}
            {!Object.keys(d.people).length && <div className="text-xs text-slate-400">Belum ada data</div>}
          </Card>
          <div className="space-y-2">{d.wos.map((w) => (
            <Card key={w.id} className="p-3" data-testid={`sla-wo-${w.code}`}>
              <div className="text-xs"><b className="font-mono">{w.code}</b> · {w.product} · keluar {t(w.created_at)} → aktif {t(w.confirmed_at)} ({mins(w.confirm_min)})</div>
              <div className="flex flex-wrap gap-1 mt-1">{w.timeline.map((s) => <span key={s.name} className={`text-[10px] px-1.5 py-0.5 rounded ${s.late && !s.excused ? "bg-rose-100 text-rose-700" : "bg-slate-100"}`}>{s.name}: {s.picked_at ? `${mins(s.wait_min)} → ${s.picked_by}` : "menunggu"}</span>)}</div>
            </Card>))}</div>
        </div>
      )}
    </Modal>
  );
};
